'use client';

import React, { useEffect, useState } from 'react';

const timelinePhases = [
  {
    phase: 'Research & shortlist',
    start: 0,
    duration: 8,
    color: 'rgba(26,26,26,0.7)',
    tasks: ['Program research', 'University comparison', 'Advisor meetings'],
  },
  {
    phase: 'Applications open',
    start: 6,
    duration: 6,
    color: 'rgba(26,26,26,0.85)',
    tasks: ['SOP writing', 'Transcripts', 'Recommendation letters'],
  },
  {
    phase: 'Visa application',
    start: 10,
    duration: 8,
    color: 'var(--tatami)',
    tasks: ['Bank statements', 'Enrollment proof', 'Embassy appointment'],
  },
  {
    phase: 'Housing search',
    start: 12,
    duration: 6,
    color: 'rgba(184,175,166,0.8)',
    tasks: ['University housing', 'Private market', 'Deposit transfer'],
  },
  {
    phase: 'Pre-departure',
    start: 16,
    duration: 4,
    color: 'var(--vermillion)',
    tasks: ['Travel insurance', 'SIM card plan', 'Currency exchange'],
  },
  {
    phase: 'Arrival & orientation',
    start: 19,
    duration: 5,
    color: 'var(--sumi)',
    tasks: ['Registration', 'Bank account', 'Course enrollment'],
  },
];

const totalWeeks = 24;
const months = ['M−6', 'M−5', 'M−4', 'M−3', 'M−2', 'M−1', 'Start'];

export default function TimelinePanel() {
  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setAnimated(true), 300);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section className="scroll-panel" style={{ background: 'var(--washi)' }}>
      <div className="noise-overlay" />
      <div className="w-full h-full flex flex-col pt-16 pb-12">

        {/* Header */}
        <div
          className="flex items-end justify-between px-8 md:px-12 pb-6"
          style={{ borderBottom: '1px solid rgba(184,175,166,0.4)' }}
        >
          <div>
            <div className="section-label mb-2">Chapter 05 — Timeline</div>
            <h2
              className="font-fraunces font-medium"
              style={{ fontSize: 'clamp(1.6rem, 2.8vw, 2.4rem)', color: 'var(--sumi)', letterSpacing: '-0.02em', lineHeight: 1.1 }}
            >
              The application timeline<br />
              <span style={{ fontStyle: 'italic' }}>most students start too late.</span>
            </h2>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div className="font-fraunces" style={{ fontSize: '42px', fontWeight: 600, color: 'var(--vermillion)', letterSpacing: '-0.03em', lineHeight: 1 }}>
              6
            </div>
            <div className="section-label">months minimum</div>
          </div>
        </div>

        {/* Gantt chart */}
        <div className="flex-1 flex flex-col justify-center px-8 md:px-12 py-6 overflow-auto">
          {/* Month labels */}
          <div style={{ display: 'flex', marginBottom: '12px', paddingLeft: '200px' }}>
            {months?.map((m, i) => (
              <div key={m} style={{ flex: 1, textAlign: 'left' }}>
                <span className="font-plex-mono" style={{ fontSize: '9px', letterSpacing: '0.1em', color: 'var(--tatami)', textTransform: 'uppercase' }}>
                  {m}
                </span>
              </div>
            ))}
          </div>

          {/* Grid lines */}
          <div style={{ position: 'relative' }}>
            <div style={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: '200px',
              right: 0,
              display: 'flex',
              pointerEvents: 'none',
            }}>
              {months?.map((_, i) => (
                <div key={i} style={{ flex: 1, borderLeft: '1px solid rgba(184,175,166,0.25)' }} />
              ))}
            </div>

            {/* Phase rows */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {timelinePhases?.map((phase, i) => (
                <div key={phase?.phase} style={{ display: 'flex', alignItems: 'center', height: '36px' }}>
                  {/* Phase label */}
                  <div style={{ width: '200px', flexShrink: 0, paddingRight: '16px' }}>
                    <div className="font-dm-sans" style={{ fontSize: '12px', fontWeight: 500, color: 'var(--sumi)', lineHeight: 1.2 }}>
                      {phase?.phase}
                    </div>
                    <div className="section-label" style={{ fontSize: '9px', color: 'var(--tatami)', marginTop: '2px' }}>
                      {phase?.tasks?.[0]}
                    </div>
                  </div>

                  {/* Gantt bar area */}
                  <div style={{ flex: 1, position: 'relative', height: '100%', display: 'flex', alignItems: 'center' }}>
                    <div
                      style={{
                        position: 'absolute',
                        left: `${(phase?.start / totalWeeks) * 100}%`,
                        width: animated ? `${(phase?.duration / totalWeeks) * 100}%` : '0%',
                        height: '14px',
                        background: phase?.color,
                        transition: `width 0.8s cubic-bezier(0.19, 1, 0.22, 1) ${i * 0.1}s`,
                        borderRadius: '1px',
                        display: 'flex',
                        alignItems: 'center',
                        paddingLeft: '6px',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <span
                        className="font-plex-mono"
                        style={{
                          fontSize: '8px',
                          letterSpacing: '0.05em',
                          color: phase?.color === 'var(--vermillion)' || phase?.color === 'var(--sumi)' ? 'var(--washi)' : 'rgba(26,26,26,0.8)',
                          textTransform: 'uppercase',
                        }}
                      >
                        {phase?.duration}wk
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <hr className="tatami-rule mt-6 mb-4" />

          <div className="flex items-center justify-between">
            <p className="font-dm-sans" style={{ fontSize: '13px', color: 'var(--sumi-light)', maxWidth: '480px', lineHeight: 1.6, fontWeight: 300 }}>
              Most students begin at M−3. The visa window alone requires M−5 for complex applications (Japan, UK, Netherlands). Starting late doesn't just add stress — it eliminates options.
            </p>
            <div style={{ textAlign: 'right' }}>
              <div className="section-label mb-1">Avg. start time surveyed</div>
              <div className="font-fraunces" style={{ fontSize: '28px', fontWeight: 600, color: 'var(--vermillion)', letterSpacing: '-0.02em' }}>
                M−2.8
              </div>
              <div className="section-label" style={{ color: 'var(--vermillion)' }}>too late for 40% of programs</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}